import { Box, Flex, HStack, Tag, Text } from "@chakra-ui/react";
import { ResponsiveBar } from "@nivo/bar";
import AdminOnlyValue from "./AdminOnlyValue";
import { getAllowancePace } from "./allowancePace";
import { formatGbp } from "./formatGbp";
import { MODEL_TIER_KEYS, MODEL_TIER_TYPES, getModelTierColor } from "./modelTiers";
import { PERSON_TYPES, PERSON_TYPE_KEYS, getPersonColor } from "./personTypes";
import { TASK_TYPES, TASK_TYPE_KEYS, getTaskTypeColor } from "./taskTypes";

const AXIS_TICK_COLOR = "#718096";
const GRID_COLOR = "#E2E8F0";
const MARKER_COLOR = "#2D3748";

const ALLOWANCE_LINE = 100;

const RISK_LABELS = {
  green: "On pace",
  amber: "Trending over",
  red: "Over pace",
};

function MockDataBadge() {
  return (
    <Tag colorScheme="orange" size="sm" fontWeight="600">
      Illustrative data
    </Tag>
  );
}

function TaskTypeLegend() {
  return (
    <HStack gap={4} flexWrap="wrap">
      {TASK_TYPES.map((t) => (
        <HStack key={t.key} gap={1.5}>
          <Box w="8px" h="8px" borderRadius="full" bg={t.color} />
          <Text fontSize="body.xs" color="gray.600">
            {t.label}
          </Text>
        </HStack>
      ))}
    </HStack>
  );
}

function ModelTierLegend() {
  return (
    <HStack gap={4} flexWrap="wrap">
      {MODEL_TIER_TYPES.map((t) => (
        <HStack key={t.key} gap={1.5}>
          <Box w="8px" h="8px" borderRadius="full" bg={t.color} />
          <Text fontSize="body.xs" color="gray.600">
            {t.label}
          </Text>
        </HStack>
      ))}
    </HStack>
  );
}

function PersonLegend() {
  return (
    <HStack gap={4} flexWrap="wrap">
      {PERSON_TYPES.map((t) => (
        <HStack key={t.key} gap={1.5}>
          <Box w="8px" h="8px" borderRadius="full" bg={t.color} />
          <Text fontSize="body.xs" color="gray.600">
            {t.label}
          </Text>
        </HStack>
      ))}
    </HStack>
  );
}

const SPLIT_TOTAL = "total";
const SPLIT_TASK = "task";
const SPLIT_TIER = "tier";
const SPLIT_PERSON = "person";

/**
 * Single horizontal bar showing usage as a % of an allowance, with a 100% marker
 * line, plus days-elapsed / days-to-reset for the period. Bar color reflects pace
 * risk (green/amber/red) — projected end-of-period usage based on how fast the
 * allowance is being used relative to how much of the period has elapsed, not just
 * raw % used. See allowancePace.js. When `splitMode` is "task", "tier" or "person",
 * the same single bar is shown as a stacked breakdown by task type, model tier or
 * person instead of one solid color. Used for the annual / current-month /
 * trailing-30-days allowance views (see AIStatus.jsx).
 *
 * @param {string} title - plain text title, ignored if `titleContent` is given
 * @param {import("react").ReactNode} [titleContent] - custom title content (e.g. a month dropdown) — takes precedence over `title`
 * @param {string} description
 * @param {number} pct - usage as a percentage of the allowance (can exceed 100)
 * @param {number} [spendGbp] - actual £ spend for the period, shown in the tooltip only when `showSpend` is set
 * @param {boolean} [showSpend] - reveal actual £ spend in the tooltip (ESG_ADMIN only — see AIStatus.jsx)
 * @param {number} daysElapsed - real calendar days elapsed in the period so far
 * @param {number} daysToReset - real calendar days remaining until the period resets
 * @param {boolean} [isMock] - whether `pct` is illustrative placeholder data
 * @param {"total"|"task"|"tier"|"person"} [splitMode] - "task"/"tier"/"person" show a stacked breakdown instead of one solid bar
 * @param {{task_type: string, pct: number}[]} [breakdown] - required when splitMode is "task"
 * @param {{tier: string, pct: number}[]} [tierBreakdown] - required when splitMode is "tier" (mock — see aiModelsAllocation.js splitPctByTier)
 * @param {{person: string, pct: number}[]} [personBreakdown] - required when splitMode is "person" (mock — see aiModelsAllocation.js splitPctByPerson)
 */
export default function AllowanceProgressChart({
  title,
  titleContent,
  description,
  pct,
  spendGbp,
  showSpend,
  daysElapsed,
  daysToReset,
  isMock,
  splitMode = SPLIT_TOTAL,
  breakdown,
  tierBreakdown,
  personBreakdown,
}) {
  const byTask = splitMode === SPLIT_TASK;
  const byTier = splitMode === SPLIT_TIER;
  const byPerson = splitMode === SPLIT_PERSON;
  const isStacked = byTask || byTier || byPerson;
  const roundedPct = Math.round(pct * 10) / 10;
  const { projectedPct, risk, color } = getAllowancePace(roundedPct, daysElapsed, daysToReset);

  const chartData = byTask
    ? [{ period: title, ...Object.fromEntries((breakdown || []).map((row) => [row.task_type, row.pct])) }]
    : byTier
      ? [{ period: title, ...Object.fromEntries((tierBreakdown || []).map((row) => [row.tier, row.pct])) }]
      : byPerson
        ? [{ period: title, ...Object.fromEntries((personBreakdown || []).map((row) => [row.person, row.pct])) }]
        : [{ period: title, pct: roundedPct }];
  const keys = byTask ? TASK_TYPE_KEYS : byTier ? MODEL_TIER_KEYS : byPerson ? PERSON_TYPE_KEYS : ["pct"];
  const maxPct = Math.max(ALLOWANCE_LINE, roundedPct);

  return (
    <Flex direction="column" gap={3}>
      <Flex justify="space-between" align="flex-start" gap={4}>
        <Box>
          {titleContent || (
            <Text fontSize="body.m" fontWeight="600" color="gray.700">
              {title}
            </Text>
          )}
          <Text fontSize="body.xs" color="gray.500">
            {description}
          </Text>
        </Box>
        {isMock && <MockDataBadge />}
      </Flex>

      <HStack gap={6}>
        <Text fontSize="body.xs" color="gray.500">
          <Text as="span" fontWeight="600" color="gray.700">
            {daysElapsed}
          </Text>{" "}
          days elapsed
        </Text>
        <Text fontSize="body.xs" color="gray.500">
          <Text as="span" fontWeight="600" color="gray.700">
            {daysToReset}
          </Text>{" "}
          days to reset
        </Text>
        {!isStacked && (
          <HStack gap={1.5}>
            <Box w="8px" h="8px" borderRadius="full" bg={color} />
            <Text fontSize="body.xs" fontWeight="600" color="gray.700">
              {RISK_LABELS[risk]}
            </Text>
          </HStack>
        )}
      </HStack>

      {byTask && <TaskTypeLegend />}
      {byTier && <ModelTierLegend />}
      {byPerson && <PersonLegend />}
      {byTier && (
        <Tag colorScheme="orange" size="sm" fontWeight="600" w="fit-content">
          Illustrative split — model-tier cost tracking isn't available yet
        </Tag>
      )}
      {byPerson && (
        <Tag colorScheme="orange" size="sm" fontWeight="600" w="fit-content">
          Illustrative split — per-person cost tracking isn't available yet
        </Tag>
      )}

      <Box height="150px" width="100%">
        <ResponsiveBar
          data={chartData}
          keys={keys}
          indexBy="period"
          layout="horizontal"
          margin={{ top: 30, right: 40, bottom: 30, left: 10 }}
          padding={0.15}
          valueScale={{ type: "linear", min: 0, max: maxPct * 1.05 }}
          indexScale={{ type: "band", round: true }}
          colors={
            byTask
              ? (bar) => getTaskTypeColor(bar.id)
              : byTier
                ? (bar) => getModelTierColor(bar.id)
                : byPerson
                  ? (bar) => getPersonColor(bar.id)
                  : () => color
          }
          borderRadius={2}
          axisTop={null}
          axisRight={null}
          axisLeft={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: 8,
          }}
          theme={{
            axis: { ticks: { text: { fill: AXIS_TICK_COLOR, fontSize: 11 } } },
            grid: { line: { stroke: GRID_COLOR } },
          }}
          enableGridX={true}
          enableGridY={false}
          enableLabel={!isStacked}
          label={(bar) => `${Math.round(bar.data.pct)}%`}
          labelTextColor="white"
          markers={[
            {
              axis: "x",
              value: ALLOWANCE_LINE,
              lineStyle: { stroke: MARKER_COLOR, strokeWidth: 1.5, strokeDasharray: "4 4" },
            },
          ]}
          layers={[
            "grid",
            "axes",
            "bars",
            "markers",
            "legends",
            ({ xScale }) => (
              <text x={xScale(ALLOWANCE_LINE)} y={-10} textAnchor="middle" fontSize={11} fill={MARKER_COLOR}>
                Allowance
              </text>
            ),
          ]}
          tooltip={({ id, value, data: rowData }) => {
            if (byTask) {
              // This segment's share of total spend, proportional to its share of pct
              // (both are derived from the same agent % allocation — see AIStatus.jsx).
              const agentSpend = showSpend && spendGbp != null && pct > 0 ? (value / pct) * spendGbp : null;
              return (
                <Box bg="white" boxShadow="md" borderRadius="md" p={2}>
                  <Text fontSize="body.s" fontWeight="600" color="gray.700">
                    {TASK_TYPES.find((t) => t.key === id)?.label || id}
                  </Text>
                  <Text fontSize="body.s" color="gray.600">
                    {Math.round(value * 10) / 10}% of allowance used
                  </Text>
                  {agentSpend !== null && <AdminOnlyValue>{formatGbp(agentSpend)}</AdminOnlyValue>}
                </Box>
              );
            }
            if (byTier) {
              // Mock split (see aiModelsAllocation.js splitPctByTier) — no real £ shown here.
              return (
                <Box bg="white" boxShadow="md" borderRadius="md" p={2}>
                  <Text fontSize="body.s" fontWeight="600" color="gray.700">
                    {MODEL_TIER_TYPES.find((t) => t.key === id)?.label || id} tier
                  </Text>
                  <Text fontSize="body.s" color="gray.600">
                    {Math.round(value * 10) / 10}% of allowance used
                  </Text>
                </Box>
              );
            }
            if (byPerson) {
              // Mock split (see aiModelsAllocation.js splitPctByPerson) — no real £ shown here.
              return (
                <Box bg="white" boxShadow="md" borderRadius="md" p={2}>
                  <Text fontSize="body.s" fontWeight="600" color="gray.700">
                    {PERSON_TYPES.find((t) => t.key === id)?.label || id}
                  </Text>
                  <Text fontSize="body.s" color="gray.600">
                    {Math.round(value * 10) / 10}% of allowance used
                  </Text>
                </Box>
              );
            }
            return (
              <Box bg="white" boxShadow="md" borderRadius="md" p={2}>
                <Text fontSize="body.s" fontWeight="600" color="gray.700">
                  {rowData.period}
                </Text>
                <Text fontSize="body.s" color="gray.600">
                  {Math.round(rowData.pct)}% of allowance used
                </Text>
                {showSpend && formatGbp(spendGbp) && <AdminOnlyValue>{formatGbp(spendGbp)}</AdminOnlyValue>}
                <Text fontSize="body.s" color={color} fontWeight="600">
                  Projected: {Math.round(projectedPct)}% by reset
                </Text>
              </Box>
            );
          }}
          role="application"
          ariaLabel={`${title} — percentage of allowance used${
            byTask ? ", by task type" : byTier ? ", by model tier" : byPerson ? ", by person" : ""
          }`}
        />
      </Box>
    </Flex>
  );
}
